import { LitElement, html, css, nothing, type PropertyValues } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import './kpi-card';
import './fp3-data-table';
import type { DataColumn } from './fp3-data-table';
import { flightMarkStyles, tailDialogStyles } from './dashboard-styles';
import { flightLabel, flightTone } from './flight-status';

type Visibility = 'private' | 'public';

interface Kpis {
  total: number;
  private_total: number;
  public_total: number;
  position_available: number;
  position_not_available: number;
  position_unchecked: number;
  registry_total?: number;
  filtered?: boolean;
  count_note?: string;
}

interface TailRow {
  n_number: string;
  registrant_name: string | null;
  city: string | null;
  state: string | null;
  manufacturer: string | null;
  model: string | null;
  visibility: string;
  visibility_reason: string;
  status_label: string | null;
  type_aircraft_label: string | null;
  position_status: string;
  online: boolean | null;
  flight_status: string | null;
  presets?: string[];
}

interface TailDetail extends TailRow {
  street: string | null;
  street2: string | null;
  zip_code: string | null;
  year_mfr: string | null;
  serial_number: string | null;
  type_registrant_label: string | null;
  origin: string | null;
  destination: string | null;
  ident: string | null;
  departed_at: string | null;
  arrived_at: string | null;
  last_position_at: string | null;
  position_summary: string | null;
  position_note?: string | null;
  position_error?: string | null;
  checked_at: string | null;
  aeroapi_configured?: boolean;
}

interface AircraftPreset {
  id: string;
  label: string;
}

interface AppStatus {
  aeroapi_configured: boolean;
  aeroapi_batch_limit: number;
  registry: {
    status: string;
    aircraft_count: number;
    error: string | null;
    running: boolean;
    message?: string;
  };
  batch: { status: string; checked: number; limit: number; error: string | null };
  visibility_help?: string;
  aircraft_filter_help?: string;
  aircraft_presets?: AircraftPreset[];
}

const DEFAULT_PRESETS: AircraftPreset[] = [
  { id: 'g550', label: 'G550' },
  { id: 'falcon2000', label: 'Falcon 2000' },
  { id: 'challenger', label: 'Bombardier Challenger' },
];

const DEFAULT_VISIBILITY_HELP =
  'Private and public come from the FAA registrant, not from FlightAware. Individual, partnership, corporation, co-owned, and LLC records are private. Government records are public. A corporation whose name looks like an airline (Airlines, Airways, Air Cargo, FedEx, Atlas Air, Kalitta, and similar) is public.';

const DEFAULT_AIRCRAFT_HELP =
  'Aircraft matching uses the FAA model text, so it is a pattern rather than an exact code. G550 matches GV-SP (G550). Falcon 2000 matches FALCON 2000 and FALCON 2000EX. Bombardier Challenger matches Canadair and Bombardier CL-600, CL-601, CL-604/605/650, and Challenger 300/350 (BD-100). CRJ regional jets and ultralights named Challenger are left out.';

const num = (value: number | undefined) => (value ?? 0).toLocaleString();

@customElement('fp3-dashboard')
export class Fp3Dashboard extends LitElement {
  @state() private kpis: Kpis | null = null;
  @state() private status: AppStatus | null = null;
  @state() private visibilities: Visibility[] = ['private'];
  @state() private models: string[] = DEFAULT_PRESETS.map((preset) => preset.id);
  @state() private query = '';
  @state() private rows: TailRow[] = [];
  @state() private catalog: TailRow[] | null = null;
  @state() private catalogComplete = false;
  @state() private catalogKpis: Kpis | null = null;
  @state() private listTotal = 0;
  @state() private offset = 0;
  @state() private selected: TailDetail | null = null;
  @state() private error = '';
  @state() private loading = false;
  @state() private checking = false;
  private limit = 5000;
  private pollTimer: number | undefined;
  private searchTimer: number | undefined;
  @query('dialog.tail-detail') private detailDialog?: HTMLDialogElement;
  @query('dialog.confirm') private confirmDialog?: HTMLDialogElement;
  @state() private confirmTail = '';

  static styles = [css`
    h1 { font-size: clamp(1.35rem, 4vw, 1.75rem); font-weight: 600; margin: 0 0 0.35rem; letter-spacing: -0.02em; }
    .subtitle { color: #8fa3b8; font-size: clamp(0.85rem, 2.5vw, 0.95rem); margin: 0 0 1.25rem; line-height: 1.5; }
    .cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 170px), 1fr)); gap: 0.75rem; margin-bottom: 1.25rem; align-items: stretch; }
    .toolbar { display: flex; flex-wrap: wrap; gap: 0.6rem; align-items: center; margin-bottom: 1rem; }
    .filter-block { flex: 1 1 100%; display: flex; flex-direction: column; gap: 0.35rem; }
    .filters { display: flex; flex-wrap: wrap; gap: 0.35rem; }
    .helper { margin: 0; max-width: 68rem; color: #8fa3b8; font-size: 0.8rem; line-height: 1.45; }
    button, input {
      font: inherit; color: #e8edf2; background: #1a2430; border: 1px solid #2a3a4a; border-radius: 8px;
    }
    button { padding: 0.45rem 0.75rem; cursor: pointer; }
    button[aria-pressed='true'], button.primary { background: #5b9fd4; color: #0f1419; border-color: #5b9fd4; font-weight: 600; }
    button:disabled { opacity: 0.55; cursor: default; }
    input { padding: 0.5rem 0.7rem; min-width: min(100%, 240px); }
    .layout { width: 100%; }
    .report { background: transparent; }
    .report-header, .detail-header {
      display: flex; justify-content: space-between; gap: 0.75rem; align-items: center;
      padding: 0.9rem 1rem; border-bottom: 1px solid #1e2a36; background: #0f1419;
    }
    .table-wrap { overflow-x: auto; }
    table { width: 100%; border-collapse: collapse; font-size: 0.88rem; min-width: 640px; }
    th, td { text-align: left; padding: 0.65rem 0.85rem; border-bottom: 1px solid #1e2a36; vertical-align: top; }
    th { color: #8fa3b8; font-weight: 500; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.04em; }
    tr.selected td { background: rgba(91, 159, 212, 0.12); }
    tbody tr { cursor: pointer; }
    tbody tr:hover td { background: rgba(91, 159, 212, 0.06); }
    .tail { font-weight: 600; color: #5b9fd4; letter-spacing: 0.02em; }
    .empty { text-align: center; color: #8fa3b8; padding: 2rem 1rem; line-height: 1.5; }
    .pager { display: flex; justify-content: space-between; gap: 0.5rem; padding: 0.75rem 1rem; color: #8fa3b8; font-size: 0.85rem; }
    .detail-scroll dl { margin: 0; }
    .detail-scroll dt { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--dash-muted, #8fa3b8); margin-top: 0.85rem; }
    .detail-scroll dd { margin: 0.2rem 0 0; line-height: 1.45; }
    .banner { margin: 0 0 1rem; padding: 0.75rem 0.9rem; border-radius: 10px; background: #1a2430; color: #c5d4e4; line-height: 1.45; }
    .banner.error { background: #2a1618; color: #f0a8a8; }
    .pill { display: inline-block; padding: 0.1rem 0.4rem; border-radius: 999px; font-size: 0.72rem; border: 1px solid #2a3a4a; }
  `, flightMarkStyles, tailDialogStyles];

  connectedCallback() {
    super.connectedCallback();
    this.refreshAll();
  }

  updated(changed: PropertyValues) {
    if (!changed.has('selected')) return;
    const dialog = this.detailDialog;
    if (!dialog) return;
    if (this.selected && !dialog.open) dialog.showModal();
    if (!this.selected && dialog.open) dialog.close();
  }

  private onDialogClose() {
    if (this.selected) this.selected = null;
  }

  private onDialogClick(event: MouseEvent) {
    const dialog = event.currentTarget as HTMLDialogElement;
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!inside) dialog.close();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopPoll();
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
  }

  private stopPoll() {
    if (this.pollTimer) window.clearInterval(this.pollTimer);
    this.pollTimer = undefined;
  }

  private ensurePoll() {
    const running = this.status?.registry.running || this.status?.batch.status === 'running';
    if (running && !this.pollTimer) {
      this.pollTimer = window.setInterval(() => this.refreshAll(false), 2000);
    }
    if (!running) this.stopPoll();
  }

  private async refreshAll(showSpinner = true) {
    if (showSpinner) this.loading = true;
    this.error = '';
    try {
      const statusPromise = fetch('/api/status');
      const dataPromise = this.loadCatalog();
      const statusRes = await statusPromise;
      if (!statusRes.ok) {
        this.error = await this.readError(statusRes);
        return;
      }
      this.status = await statusRes.json();
      await dataPromise;
      this.ensurePoll();
    } catch {
      this.error = 'The API is not reachable. Start the backend and Postgres, then reload.';
    } finally {
      this.loading = false;
    }
  }

  /** G550, Falcon 2000, and Challenger for both visibilities fit in one response. */
  private usingCatalog() {
    const known = DEFAULT_PRESETS.map((preset) => preset.id);
    return this.catalogComplete && this.catalog !== null && this.models.length > 0
      && this.models.every((id) => known.includes(id));
  }

  private filterParams(includeList: boolean) {
    const params = new URLSearchParams();
    if (includeList) {
      params.set('limit', String(this.limit));
      params.set('offset', String(this.offset));
    }
    params.set('visibility', this.visibilities.length ? this.visibilities.join(',') : 'all');
    const term = this.query.trim();
    if (term.length >= 3) params.set('q', term);
    if (this.models.length) params.set('models', this.models.join(','));
    return params;
  }

  private async loadCatalog() {
    const ids = DEFAULT_PRESETS.map((preset) => preset.id).join(',');
    const listPromise = fetch(`/api/tails?limit=5000&offset=0&visibility=all&models=${ids}`);
    const kpiPromise = fetch(`/api/kpis?visibility=private&models=${ids}`);
    const res = await listPromise;
    if (!res.ok) {
      this.error = await this.readError(res);
      return;
    }
    const body = await res.json();
    this.catalog = body.tails;
    this.catalogComplete = body.tails.length >= body.total;
    if (!this.usingCatalog()) await this.loadList();
    const kpiRes = await kpiPromise;
    if (kpiRes.ok) this.catalogKpis = await kpiRes.json();
  }

  private async loadList() {
    const [res, kpiRes] = await Promise.all([
      fetch(`/api/tails?${this.filterParams(true)}`),
      fetch(`/api/kpis?${this.filterParams(false)}`),
    ]);
    if (!res.ok || !kpiRes.ok) {
      this.error = await this.readError(res.ok ? kpiRes : res);
      return;
    }
    const body = await res.json();
    this.rows = body.tails;
    this.listTotal = body.total;
    this.kpis = await kpiRes.json();
  }

  private matchesQuery(row: TailRow, query: string) {
    const text = query.trim();
    if (!text) return true;
    const folded = text.toLowerCase();
    const compact = text.replace(/\s/g, '').toUpperCase();
    const prefix = compact.startsWith('N') ? compact : `N${compact}`;
    const tail = row.n_number.toUpperCase();
    return tail.startsWith(prefix)
      || tail.toLowerCase().includes(folded)
      || (row.registrant_name || '').toLowerCase().includes(folded);
  }

  private get viewRows() {
    if (!this.usingCatalog() || !this.catalog) return this.rows;
    return this.catalog.filter((row) => {
      if (this.visibilities.length && !this.visibilities.includes(row.visibility as Visibility)) return false;
      if (!this.models.some((id) => (row.presets || []).includes(id))) return false;
      return this.matchesQuery(row, this.query);
    });
  }

  private get viewKpis(): Kpis | null {
    if (!this.usingCatalog()) return this.kpis;
    const rows = this.viewRows;
    const available = rows.filter((row) => row.position_status === 'available').length;
    const notAvailable = rows.filter((row) => row.position_status === 'blocked' || row.position_status === 'unavailable').length;
    const known = DEFAULT_PRESETS.map((preset) => preset.id);
    const isDefault = this.visibilities.length === 1 && this.visibilities[0] === 'private'
      && known.every((id) => this.models.includes(id))
      && !this.query.trim();
    return {
      total: rows.length,
      private_total: rows.filter((row) => row.visibility === 'private').length,
      public_total: rows.filter((row) => row.visibility === 'public').length,
      position_available: available,
      position_not_available: notAvailable,
      position_unchecked: Math.max(0, rows.length - available - notAvailable),
      registry_total: this.catalogKpis?.registry_total,
      filtered: !isDefault,
      count_note: isDefault ? this.catalogKpis?.count_note : undefined,
    };
  }

  private patchListed(detail: TailDetail) {
    const previous = (this.catalog || this.rows).find((row) => row.n_number === detail.n_number);
    const apply = (rows: TailRow[]) => {
      const index = rows.findIndex((row) => row.n_number === detail.n_number);
      if (index < 0) return rows;
      const next = rows.slice();
      next[index] = {
        ...rows[index],
        position_status: detail.position_status,
        online: detail.online,
        flight_status: detail.flight_status,
      };
      return next;
    };
    if (this.catalog) this.catalog = apply(this.catalog);
    this.rows = apply(this.rows);
    if (!this.usingCatalog() && this.kpis && previous && previous.position_status !== detail.position_status) {
      this.kpis = this.shiftPositionKpis(this.kpis, previous.position_status, detail.position_status);
    }
  }

  private shiftPositionKpis(kpis: Kpis, previous: string, next: string): Kpis {
    const available = (status: string) => status === 'available' ? 1 : 0;
    const blocked = (status: string) => status === 'blocked' || status === 'unavailable' ? 1 : 0;
    const positionAvailable = kpis.position_available + available(next) - available(previous);
    const positionNotAvailable = kpis.position_not_available + blocked(next) - blocked(previous);
    return {
      ...kpis,
      position_available: positionAvailable,
      position_not_available: positionNotAvailable,
      position_unchecked: Math.max(0, kpis.total - positionAvailable - positionNotAvailable),
    };
  }

  private async readError(res: Response) {
    try {
      const body = await res.json();
      return body.detail || `Request failed (${res.status})`;
    } catch {
      return `Request failed (${res.status})`;
    }
  }

  private setVisibilities(next: Visibility[]) {
    this.visibilities = next;
    this.offset = 0;
    this.selected = null;
    if (!this.usingCatalog()) this.loadList();
  }

  private toggleVisibility(id: Visibility) {
    const next = this.visibilities.includes(id)
      ? this.visibilities.filter((item) => item !== id)
      : [...this.visibilities, id];
    this.setVisibilities(next);
  }

  private setModels(next: string[]) {
    this.models = next;
    this.offset = 0;
    this.selected = null;
    if (!this.usingCatalog()) this.loadList();
  }

  private toggleModel(id: string) {
    const next = this.models.includes(id) ? this.models.filter((item) => item !== id) : [...this.models, id];
    this.setModels(next);
  }

  private onSearch(e: Event) {
    const value = (e.target as HTMLInputElement).value;
    const term = value.trim();
    if (term.length === 1 || term.length === 2) {
      if (this.query.trim()) this.commitSearch('');
      return;
    }
    this.commitSearch(value);
  }

  private commitSearch(value: string) {
    if (value === this.query) return;
    this.query = value;
    this.offset = 0;
    if (this.usingCatalog()) return;
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => this.loadList(), 250);
  }

  private openFlightAware(nNumber: string) {
    window.open(`https://www.flightaware.com/live/flight/${encodeURIComponent(nNumber)}`, '_blank', 'noopener');
    this.confirmTail = nNumber;
    this.updateComplete.then(() => {
      if (!this.confirmDialog?.open) this.confirmDialog?.showModal();
    });
  }

  private async answerVisibility(retrievable: boolean) {
    const nNumber = this.confirmTail;
    this.confirmDialog?.close();
    if (!nNumber) return;
    const res = await fetch(`/api/tails/${encodeURIComponent(nNumber)}/flight-visibility`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ retrievable }),
    });
    if (!res.ok) {
      this.error = await this.readError(res);
      return;
    }
    this.selected = await res.json();
    if (this.selected) this.patchListed(this.selected);
  }

  private async selectTail(nNumber: string) {
    this.checking = true;
    try {
      const res = await fetch(`/api/tails/${encodeURIComponent(nNumber)}`);
      if (!res.ok) {
        this.error = await this.readError(res);
        return;
      }
      const detail: TailDetail = await res.json();
      this.selected = detail;
      if (detail.position_status === 'unchecked' && this.status?.aeroapi_configured) {
        await this.checkPosition(nNumber, false);
      }
    } finally {
      this.checking = false;
    }
  }

  private async checkPosition(nNumber: string, force: boolean) {
    this.checking = true;
    try {
      const res = await fetch(`/api/tails/${encodeURIComponent(nNumber)}/position?force=${force ? 'true' : 'false'}`, {
        method: 'POST',
      });
      if (!res.ok) {
        this.error = await this.readError(res);
        return;
      }
      this.selected = await res.json();
      if (this.selected) this.patchListed(this.selected);
    } finally {
      this.checking = false;
    }
  }

  private async loadRegistry() {
    const res = await fetch('/api/registry/refresh', { method: 'POST' });
    if (!res.ok) {
      this.error = await this.readError(res);
      return;
    }
    await this.refreshAll(false);
  }

  private async runBatch() {
    const res = await fetch('/api/position/batch', { method: 'POST' });
    if (!res.ok) {
      this.error = await this.readError(res);
      return;
    }
    const body = await res.json();
    if (!body.started && body.reason) this.error = body.reason;
    await this.refreshAll(false);
  }

  private listTitle() {
    const scope = this.visibilities.length === 1
      ? (this.visibilities[0] === 'public' ? 'Public tails' : 'Private tails')
      : 'All tails';
    if (!this.models.length) return scope;
    const presets = this.status?.aircraft_presets?.length ? this.status.aircraft_presets : DEFAULT_PRESETS;
    const labels = presets.filter((preset) => this.models.includes(preset.id)).map((preset) => preset.label);
    return labels.length ? `${scope} · ${labels.join(', ')}` : scope;
  }

  private positionMark(status: string | undefined) {
    const label = flightLabel(status);
    return html`<span class="flight-mark ${flightTone(status)}"><span class="flight-dot" aria-hidden="true"></span>${label}</span>`;
  }

  private renderDetail() {
    const tail = this.selected;
    if (!tail) return nothing;
    const place = [tail.city, tail.state, tail.zip_code].filter(Boolean).join(', ');
    const route = [tail.origin, tail.destination].filter(Boolean).join(' → ');
    return html`
      <div class="detail-head">
        <div>
          <p class="detail-kicker">Tail</p>
          <h3 class="tail">${tail.n_number}</h3>
          <div class="detail-sub">${this.positionMark(tail.position_status)}</div>
        </div>
        <div class="detail-tools">
          <button type="button" class="external" @click=${() => this.openFlightAware(tail.n_number)}>Open in FlightAware</button>
          <button type="button" class="dialog-close" @click=${() => this.detailDialog?.close()}>Close</button>
        </div>
      </div>
      <div class="detail-scroll">
        <p class="detail-note">FlightAware opens in a new tab. After you look, choose whether this tail is retrievable or blocked. The page itself is not copied.</p>
        <dl>
          <dt>Point of contact</dt>
          <dd>
            ${tail.registrant_name || '—'}<br />
            ${tail.street || ''}${tail.street2 ? html`<br />${tail.street2}` : nothing}<br />
            ${place || '—'}
          </dd>
          <dt>Aircraft</dt>
          <dd>
            ${[tail.manufacturer, tail.model].filter(Boolean).join(' ') || '—'}<br />
            ${tail.type_aircraft_label || 'Type unknown'}
            ${tail.year_mfr ? html` · ${tail.year_mfr}` : nothing}
            ${tail.serial_number ? html`<br />Serial ${tail.serial_number}` : nothing}
          </dd>
          <dt>Registry status</dt>
          <dd>${tail.status_label || '—'} · ${tail.visibility_reason} · ${tail.visibility}</dd>
          <dt>Online / position</dt>
          <dd>
            ${tail.online ? 'Online' : 'Not reporting a live position'}
            ${tail.flight_status ? html`<br />${tail.flight_status}` : nothing}
            ${route ? html`<br />${route}` : nothing}
            ${tail.last_position_at ? html`<br />Last position ${tail.last_position_at}` : nothing}
            ${tail.position_summary ? html`<br />${tail.position_summary}` : nothing}
            ${tail.position_note ? html`<br />${tail.position_note}` : nothing}
            ${tail.position_error ? html`<br />${tail.position_error}` : nothing}
          </dd>
        </dl>
        <div class="toolbar" style="padding-top: 0.9rem;">
          <button
            type="button"
            class="primary"
            ?disabled=${this.checking || !this.status?.aeroapi_configured}
            @click=${() => this.checkPosition(tail.n_number, true)}
          >
            ${this.checking ? 'Checking…' : 'Recheck AeroAPI'}
          </button>
        </div>
      </div>
    `;
  }

  private get tailColumns(): DataColumn<TailRow>[] {
    return [
      { key: 'tail', label: 'Tail', emphasis: true, value: (row) => row.n_number },
      { key: 'registrant', label: 'Registrant', value: (row) => row.registrant_name || '' },
      {
        key: 'aircraft',
        label: 'Aircraft',
        value: (row) => [row.manufacturer, row.model].filter(Boolean).join(' ') || row.type_aircraft_label || '',
      },
      { key: 'place', label: 'Place', value: (row) => [row.city, row.state].filter(Boolean).join(', ') },
      {
        key: 'position',
        label: 'Position',
        filter: 'select',
        value: (row) => flightLabel(row.position_status),
        mark: (row) => flightTone(row.position_status),
      },
    ];
  }

  render() {
    const k = this.viewKpis;
    const listed = this.viewRows;
    const listedTotal = this.usingCatalog() ? listed.length : this.listTotal;
    const listedOffset = this.usingCatalog() ? 0 : this.offset;
    const registry = this.status?.registry;
    const emptyRegistry = !registry || registry.aircraft_count === 0;
    return html`
      <h1>US tail directory</h1>
      <p class="subtitle">
        Private tails are shown by default. Registrants come from the FAA Releasable Aircraft Database.
        Position coverage is checked per tail with FlightAware AeroAPI, not from a blocked-aircraft list.
      </p>
      ${this.error ? html`<div class="banner error">${this.error}</div>` : nothing}
      ${emptyRegistry
        ? html`<div class="banner">
            The FAA registry is not loaded yet. Load it to pull every US tail from the daily
            Releasable Aircraft ZIP (MASTER.txt). The file is large and the first load takes several minutes.
            ${registry?.error ? html`<br />${registry.error}` : nothing}
          </div>`
        : nothing}
      ${this.status && !this.status.aeroapi_configured
        ? html`<div class="banner">
            FLIGHTAWARE_API_KEY is not set. Tails stay unchecked, and unchecked is not counted as blocked.
          </div>`
        : nothing}

      <section class="cards">
        <kpi-card compact
          label="Total tails"
          value=${k ? num(k.total) : ''}
          sub=${k?.count_note || (k?.filtered && k.registry_total != null ? `of ${num(k.registry_total)} in the registry` : '')}
          tooltip=${k?.count_note || 'Tails in the current aircraft and search filter.'}
        ></kpi-card>
        <kpi-card compact
          label="Private"
          value=${k ? num(k.private_total) : ''}
          sub=${k?.filtered ? 'In the selected filter' : ''}
          tooltip="Individual, partnership, corporation, co-owned, and LLC registrants."
          accent="#7eb8c9"
        ></kpi-card>
        <kpi-card compact
          label="Public"
          value=${k ? num(k.public_total) : ''}
          sub=${k?.filtered ? 'In the selected filter' : ''}
          tooltip="Government records and airline-style corporation names."
          accent="#d4a843"
        ></kpi-card>
        <kpi-card compact
          label="Can pull position"
          value=${k ? num(k.position_available) : ''}
          tooltip="Retrievable on this tail: marked after FlightAware, or returned by AeroAPI."
          accent="#8fd0a8"
        ></kpi-card>
        <kpi-card compact
          label="Blocked / not available"
          value=${k ? num(k.position_not_available) : ''}
          sub=${k ? `${num(k.position_unchecked)} unchecked` : ''}
          tooltip="Hidden after FlightAware, or reported blocked or unavailable. Unchecked tails are not counted."
          accent="#e07a7a"
        ></kpi-card>
      </section>

      <div class="toolbar">
        <div class="filter-block">
          <div class="filters" role="group" aria-label="Visibility">
            <button type="button" aria-pressed=${this.visibilities.length === 0} @click=${() => this.setVisibilities([])}>
              All
            </button>
            ${(['private', 'public'] as Visibility[]).map(
              (item) => html`
                <button type="button" aria-pressed=${this.visibilities.includes(item)} @click=${() => this.toggleVisibility(item)}>
                  ${item === 'private' ? 'Private' : 'Public'}
                </button>
              `,
            )}
          </div>
          <p class="helper">${this.status?.visibility_help || DEFAULT_VISIBILITY_HELP}</p>
        </div>
        <div class="filter-block">
          <div class="filters" role="group" aria-label="Aircraft">
            <button type="button" aria-pressed=${this.models.length === 0} @click=${() => this.setModels([])}>
              All aircraft
            </button>
            ${(this.status?.aircraft_presets?.length ? this.status.aircraft_presets : DEFAULT_PRESETS).map(
              (preset) => html`
                <button
                  type="button"
                  aria-pressed=${this.models.includes(preset.id)}
                  @click=${() => this.toggleModel(preset.id)}
                >
                  ${preset.label}
                </button>
              `,
            )}
          </div>
          <p class="helper">${this.status?.aircraft_filter_help || DEFAULT_AIRCRAFT_HELP}</p>
        </div>
        <input type="search" placeholder="Tail or registrant" @input=${this.onSearch} />
        <button type="button" class="primary" ?disabled=${registry?.running} @click=${() => this.loadRegistry()}>
          ${registry?.running ? 'Loading FAA registry…' : 'Load FAA registry'}
        </button>
        <button type="button" ?disabled=${!this.status?.aeroapi_configured || this.status?.batch.status === 'running'} @click=${() => this.runBatch()}>
          ${this.status?.batch.status === 'running'
            ? `Checking ${this.status.batch.checked}/${this.status.batch.limit}…`
            : `Check next ${this.status?.aeroapi_batch_limit ?? 25}`}
        </button>
      </div>

      <div class="layout">
        <section class="report">
          <div class="report-header">
            <h2 style="margin:0;font-size:1rem;">${this.listTitle()}</h2>
            <span>${num(listedTotal)}</span>
          </div>
          <fp3-data-table
            fill
            .columns=${this.tailColumns}
            .rows=${listed}
            keyField="n_number"
            selectedKey=${this.selected?.n_number ?? ''}
            empty=${this.loading
              ? 'Loading…'
              : emptyRegistry
                ? 'No tails yet. Load the FAA registry to fill this list.'
                : 'No tails match this filter.'}
            @select=${(event: CustomEvent<TailRow>) => this.selectTail(event.detail.n_number)}
          ></fp3-data-table>
          <div class="pager">
            <button type="button" ?disabled=${listedOffset === 0} @click=${() => { this.offset = Math.max(0, this.offset - this.limit); this.loadList(); }}>Previous</button>
            <span>${listedTotal ? `${listedOffset + 1}–${Math.min(listedOffset + listed.length, listedTotal)} of ${num(listedTotal)}` : '0'}</span>
            <button type="button" ?disabled=${listedOffset + this.limit >= listedTotal} @click=${() => { this.offset += this.limit; this.loadList(); }}>Next</button>
          </div>
        </section>
      </div>
      <dialog class="tail-detail" @close=${this.onDialogClose} @click=${this.onDialogClick}>
        ${this.renderDetail()}
      </dialog>
      <dialog class="confirm" @click=${this.onDialogClick}>
        <p class="detail-kicker">FlightAware</p>
        <h3>Is flight information for ${this.confirmTail} retrievable?</h3>
        <p class="detail-note">Yes stores this tail as retrievable. No stores it as blocked. This is your answer, not a copy of the FlightAware page.</p>
        <div class="confirm-actions">
          <button type="button" class="yes" @click=${() => this.answerVisibility(true)}>Yes, retrievable</button>
          <button type="button" @click=${() => this.answerVisibility(false)}>No, blocked</button>
        </div>
      </dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fp3-dashboard': Fp3Dashboard;
  }
}
